const IDLE_DETECTION_PERMISSION = "idle-detection";

export const isSystemIdleDetectionSupported = () =>
  typeof window !== "undefined" && typeof window.IdleDetector === "function";

export const getSystemIdlePermissionState = async () => {
  if (!isSystemIdleDetectionSupported()) return "unsupported";
  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({
        name: IDLE_DETECTION_PERMISSION,
      });
      return status?.state || "prompt";
    }
  } catch (_error) {
    // Some browsers reject querying this permission name.
  }
  return "prompt";
};

/** Must be called from a user gesture (e.g. Check In). */
export const requestSystemIdlePermission = async () => {
  if (!isSystemIdleDetectionSupported()) return "unsupported";
  try {
    if (typeof window.IdleDetector.requestPermission === "function") {
      return await window.IdleDetector.requestPermission();
    }
  } catch (_error) {
    return "denied";
  }
  return "denied";
};

const resolveIdlePermission = async () => {
  const queried = await getSystemIdlePermissionState();
  if (queried === "granted" || queried === "denied" || queried === "unsupported") {
    return queried;
  }
  // Do not call requestPermission here — it requires a user gesture.
  return queried;
};

/**
 * Starts Chromium Idle Detection (PC / device level mouse, keyboard, screen lock).
 * Permission must already be granted via requestSystemIdlePermission() on Check In.
 */
export const startSystemIdleDetector = async ({
  thresholdMs = 60_000,
  onChange,
} = {}) => {
  if (!isSystemIdleDetectionSupported()) return null;

  const permission = await resolveIdlePermission();
  if (permission !== "granted") return null;

  const controller = new AbortController();
  const detector = new window.IdleDetector();

  detector.addEventListener("change", () => {
    onChange?.({
      userState: detector.userState,
      screenState: detector.screenState,
    });
  });

  try {
    await detector.start({
      threshold: Math.max(60_000, thresholdMs),
      signal: controller.signal,
    });
    onChange?.({
      userState: detector.userState,
      screenState: detector.screenState,
    });
    return { controller, detector };
  } catch (_error) {
    controller.abort();
    return null;
  }
};
